import axios, { type AxiosInstance, type AxiosError } from 'axios';
import { getApiDomain } from '@/config/apiDomains';

/**
 * API エラーレスポンスの型
 */
export interface ApiErrorResponse {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

/**
 * カスタム API エラー
 */
export class ApiError extends Error {
  code: string;
  status: number;
  details?: Record<string, unknown>;

  constructor(
    message: string,
    code: string,
    status: number,
    details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }

  static fromAxiosError(error: AxiosError<ApiErrorResponse>): ApiError {
    if (error.response) {
      const { data, status } = error.response;
      return new ApiError(
        data?.message || error.message,
        data?.code || 'UNKNOWN_ERROR',
        status,
        data?.details
      );
    }

    if (error.request) {
      return new ApiError(
        'ネットワークエラーが発生しました。接続を確認してください。',
        'NETWORK_ERROR',
        0
      );
    }

    return new ApiError(error.message, 'REQUEST_ERROR', 0);
  }
}

/**
 * API クライアントの作成
 *
 * APIドメインは以下の優先順位で決定:
 * 1. URLパラメータ (?env=dev|stg|fl001|fl002)
 * 2. 環境変数 (VITE_API_BASE_URL)
 * 3. デフォルト (dev環境)
 */
const createApiClient = (): AxiosInstance => {
  const baseURL = getApiDomain();

  const client = axios.create({
    baseURL,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // リクエストインターセプター: 毎回最新のAPIドメインを使用
  // LIFF SDKのOAuthリダイレクト後にURLパラメータが遅延解決されるケースに対応
  client.interceptors.request.use(
    (config) => {
      config.baseURL = getApiDomain();
      return config;
    },
    (error) => Promise.reject(error)
  );

  // レスポンスインターセプター
  client.interceptors.response.use(
    (response) => response,
    (error: AxiosError<ApiErrorResponse>) => {
      console.error('API Error:', error);
      throw ApiError.fromAxiosError(error);
    }
  );

  return client;
};

export const apiClient = createApiClient();

export default apiClient;
